/**
 * Compact stores for `ACAD_TABLE` (AcDbTable) and `OLE2FRAME`
 * (AcDbOle2Frame).
 *
 * Both exist for the same reason as `compactHeavyPolylineStore.ts`: on the
 * streaming import path `space.entities[]` is discarded and the read-through
 * document is rebuilt from the compact store, so an entity type absent from
 * `appendEntity`'s switch is silently dropped in the browser (the
 * `counts.Other++` / `return -1` arm) while every non-streaming test still
 * passes.
 *
 * ## Variable-length payloads
 *
 * A table's cell records (`contentGroups`) and an OLE frame's compound-
 * document chunks (`binaryChunks`) are opaque, retained verbatim for
 * round-trip, and never read field-by-field by the renderer. They are held in
 * `Map`s keyed by entity index — the same sparse-payload shape
 * `compactVertexPacking.ts` uses for `vertexIdData: Map<number, number[]>`
 * and `compactPolylineStores.ts` for its tangent/extrusion maps — rather than
 * being flattened into typed arrays that would have to be re-parsed on every
 * read. Packing an OLE payload's megabytes of hex into a numeric column would
 * cost more than it saves and could not round-trip the chunk boundaries the
 * writer re-emits.
 *
 * ## Presence, not sentinels
 *
 * Every optional group carries a presence bit: `version` absent and
 * `version = 0` are different files, and a store that collapsed them would
 * corrupt export on the streaming path only.
 */

import type {
  DrawingPoint3D,
  DxfGroupRecord,
  OleFrameEntity,
  TableEntity,
} from "@dxf-ai-editor/drawing-model";

import {
  BASE_BYTES_PER_ENTITY,
  createBaseColumns,
  growBaseColumns,
  readBase,
  writeBase,
  type BaseColumns,
} from "./compactStoreBase";
import { internString, lookupString, type SharedStringTable } from "./sharedStringTable";

/* ── ACAD_TABLE ───────────────────────────────────────────────────────── */

/** Per-entity floats: insertion point x/y/z. */
const TBL_FLOATS = 3;
/** Per-entity ints: version, valueFlag, row/column count, 4 override flags, cached byte count. */
const TBL_INTS = 9;

const TBL_HAS_VERSION = 1 << 0;
const TBL_HAS_VALUE_FLAG = 1 << 1;
const TBL_HAS_ROW_COUNT = 1 << 2;
const TBL_HAS_COLUMN_COUNT = 1 << 3;
const TBL_HAS_OVERRIDE = 1 << 4;
const TBL_HAS_BORDER_COLOR_OVERRIDE = 1 << 5;
const TBL_HAS_BORDER_LINEWEIGHT_OVERRIDE = 1 << 6;
const TBL_HAS_BORDER_VISIBILITY_OVERRIDE = 1 << 7;
const TBL_HAS_CACHED_BYTE_COUNT = 1 << 8;

export interface CompactTableStore {
  count: number;
  base: BaseColumns;
  floats: Float32Array;
  ints: Int32Array;
  /** `TBL_HAS_*` presence bits — 9 of them, so 16 bits wide. */
  present: Uint16Array;
  /** Interned `blockName` — the anonymous `*T<N>` graphics block. */
  blockNames: Uint16Array;
  /** Sparse — 11/21/31, absent ⇒ the (1, 0, 0) default. */
  horizontalDirections: Map<number, DrawingPoint3D>;
  /** Sparse — 342 / 343 hard-pointer handles. */
  tableStyleHandles: Map<number, string>;
  blockRecordHandles: Map<number, string>;
  /** Read-only 141/142 projections, kept so the panel reads the same values. */
  rowHeights: Map<number, number[]>;
  columnWidths: Map<number, number[]>;
  /** The verbatim AcDbTable body — the single write source for export. */
  contentGroups: Map<number, DxfGroupRecord[]>;
  /** 310 cached-graphics chunks, retained exactly as authored. */
  cachedGraphicsChunks: Map<number, string[]>;
}

export function createCompactTableStore(initialCapacity = 16): CompactTableStore {
  const cap = Math.max(1, initialCapacity);
  return {
    count: 0,
    base: createBaseColumns(cap),
    floats: new Float32Array(cap * TBL_FLOATS),
    ints: new Int32Array(cap * TBL_INTS),
    present: new Uint16Array(cap),
    blockNames: new Uint16Array(cap),
    horizontalDirections: new Map(),
    tableStyleHandles: new Map(),
    blockRecordHandles: new Map(),
    rowHeights: new Map(),
    columnWidths: new Map(),
    contentGroups: new Map(),
    cachedGraphicsChunks: new Map(),
  };
}

function ensureTableCap(s: CompactTableStore, needed: number): void {
  if (needed <= s.base.capacity) return;
  const c = growBaseColumns(s.base, needed);
  const f = new Float32Array(c * TBL_FLOATS); f.set(s.floats);
  const i = new Int32Array(c * TBL_INTS); i.set(s.ints);
  const p = new Uint16Array(c); p.set(s.present);
  const b = new Uint16Array(c); b.set(s.blockNames);
  s.floats = f; s.ints = i; s.present = p; s.blockNames = b;
}

/** Store `value` at `index` when present, else clear any stale entry. */
function setSparse<T>(map: Map<number, T>, index: number, value: T | undefined): void {
  if (value !== undefined) map.set(index, value);
  else map.delete(index);
}

export function appendTable(
  s: CompactTableStore,
  table: SharedStringTable,
  e: TableEntity,
): number {
  const index = s.count;
  ensureTableCap(s, index + 1);
  s.count++;
  writeBase(s.base, table, index, e);

  const f = index * TBL_FLOATS;
  s.floats[f + 0] = e.insertionPoint.x;
  s.floats[f + 1] = e.insertionPoint.y;
  s.floats[f + 2] = e.insertionPoint.z;

  const n = index * TBL_INTS;
  s.ints[n + 0] = e.version ?? 0;
  s.ints[n + 1] = e.valueFlag ?? 0;
  s.ints[n + 2] = e.rowCount ?? 0;
  s.ints[n + 3] = e.columnCount ?? 0;
  s.ints[n + 4] = e.overrideFlag ?? 0;
  s.ints[n + 5] = e.borderColorOverrideFlag ?? 0;
  s.ints[n + 6] = e.borderLineweightOverrideFlag ?? 0;
  s.ints[n + 7] = e.borderVisibilityOverrideFlag ?? 0;
  s.ints[n + 8] = e.cachedGraphicsByteCount ?? 0;

  let present = 0;
  if (e.version !== undefined) present |= TBL_HAS_VERSION;
  if (e.valueFlag !== undefined) present |= TBL_HAS_VALUE_FLAG;
  if (e.rowCount !== undefined) present |= TBL_HAS_ROW_COUNT;
  if (e.columnCount !== undefined) present |= TBL_HAS_COLUMN_COUNT;
  if (e.overrideFlag !== undefined) present |= TBL_HAS_OVERRIDE;
  if (e.borderColorOverrideFlag !== undefined) {
    present |= TBL_HAS_BORDER_COLOR_OVERRIDE;
  }
  if (e.borderLineweightOverrideFlag !== undefined) {
    present |= TBL_HAS_BORDER_LINEWEIGHT_OVERRIDE;
  }
  if (e.borderVisibilityOverrideFlag !== undefined) {
    present |= TBL_HAS_BORDER_VISIBILITY_OVERRIDE;
  }
  if (e.cachedGraphicsByteCount !== undefined) {
    present |= TBL_HAS_CACHED_BYTE_COUNT;
  }
  s.present[index] = present;

  s.blockNames[index] = internString(table, e.blockName);
  setSparse(s.horizontalDirections, index, e.horizontalDirection);
  setSparse(s.tableStyleHandles, index, e.tableStyleHandle);
  setSparse(s.blockRecordHandles, index, e.blockRecordHandle);
  s.rowHeights.set(index, e.rowHeights);
  s.columnWidths.set(index, e.columnWidths);
  s.contentGroups.set(index, e.contentGroups);
  s.cachedGraphicsChunks.set(index, e.cachedGraphicsChunks);
  return index;
}

export function tableFromCompact(
  s: CompactTableStore,
  table: SharedStringTable,
  index: number,
): TableEntity {
  if (index < 0 || index >= s.count) {
    throw new RangeError(
      `tableFromCompact: index ${index} out of range (count=${s.count})`,
    );
  }
  const f = index * TBL_FLOATS;
  const n = index * TBL_INTS;
  const present = s.present[index] ?? 0;
  const entity: TableEntity = {
    ...readBase(s.base, table, index),
    type: "Table",
    blockName: lookupString(table, s.blockNames[index] ?? 0),
    insertionPoint: {
      x: s.floats[f + 0]!,
      y: s.floats[f + 1]!,
      z: s.floats[f + 2]!,
    },
    rowHeights: s.rowHeights.get(index) ?? [],
    columnWidths: s.columnWidths.get(index) ?? [],
    contentGroups: s.contentGroups.get(index) ?? [],
    cachedGraphicsChunks: s.cachedGraphicsChunks.get(index) ?? [],
  };
  const horizontalDirection = s.horizontalDirections.get(index);
  if (horizontalDirection !== undefined) {
    entity.horizontalDirection = horizontalDirection;
  }
  if ((present & TBL_HAS_VERSION) !== 0) entity.version = s.ints[n + 0]!;
  const tableStyleHandle = s.tableStyleHandles.get(index);
  if (tableStyleHandle !== undefined) entity.tableStyleHandle = tableStyleHandle;
  const blockRecordHandle = s.blockRecordHandles.get(index);
  if (blockRecordHandle !== undefined) {
    entity.blockRecordHandle = blockRecordHandle;
  }
  if ((present & TBL_HAS_VALUE_FLAG) !== 0) entity.valueFlag = s.ints[n + 1]!;
  if ((present & TBL_HAS_ROW_COUNT) !== 0) entity.rowCount = s.ints[n + 2]!;
  if ((present & TBL_HAS_COLUMN_COUNT) !== 0) {
    entity.columnCount = s.ints[n + 3]!;
  }
  if ((present & TBL_HAS_OVERRIDE) !== 0) entity.overrideFlag = s.ints[n + 4]!;
  if ((present & TBL_HAS_BORDER_COLOR_OVERRIDE) !== 0) {
    entity.borderColorOverrideFlag = s.ints[n + 5]!;
  }
  if ((present & TBL_HAS_BORDER_LINEWEIGHT_OVERRIDE) !== 0) {
    entity.borderLineweightOverrideFlag = s.ints[n + 6]!;
  }
  if ((present & TBL_HAS_BORDER_VISIBILITY_OVERRIDE) !== 0) {
    entity.borderVisibilityOverrideFlag = s.ints[n + 7]!;
  }
  if ((present & TBL_HAS_CACHED_BYTE_COUNT) !== 0) {
    entity.cachedGraphicsByteCount = s.ints[n + 8]!;
  }
  return entity;
}

export function estimateTableBytesPerEntity(s: CompactTableStore): number {
  const fixed = TBL_FLOATS * 4 + TBL_INTS * 4 + 2 + 2 + BASE_BYTES_PER_ENTITY;
  if (s.count === 0) return fixed;
  let payloadChars = 0;
  for (const chunks of s.cachedGraphicsChunks.values()) {
    for (let i = 0; i < chunks.length; i++) payloadChars += chunks[i]!.length;
  }
  let groups = 0;
  for (const g of s.contentGroups.values()) groups += g.length;
  // 2 bytes per UTF-16 char of retained hex, plus a conservative 32 bytes per
  // retained group record (code + value string header).
  return fixed + (payloadChars * 2 + groups * 32) / s.count;
}

/* ── OLE2FRAME ────────────────────────────────────────────────────────── */

/** Per-entity floats: upper-left x/y/z then lower-right x/y/z. */
const OLE_FLOATS = 6;
/** Per-entity ints: version, object type, tile mode, draw aspect, byte count. */
const OLE_INTS = 5;

const OLE_HAS_VERSION = 1 << 0;
const OLE_HAS_OBJECT_TYPE = 1 << 1;
const OLE_HAS_TILE_MODE = 1 << 2;
const OLE_HAS_DRAW_ASPECT = 1 << 3;
const OLE_HAS_BYTE_COUNT = 1 << 4;

export interface CompactOleFrameStore {
  count: number;
  base: BaseColumns;
  floats: Float32Array;
  ints: Int32Array;
  present: Uint8Array;
  /** Sparse — group 3 object-type string and the group 1 "OLE" terminator. */
  objectDescriptions: Map<number, string>;
  terminators: Map<number, string>;
  /**
   * The OLE compound document (group 310), one entry per authored DXF chunk.
   * NEVER decoded — see `oleFrameEntitySchema`'s doc — so it is retained
   * exactly as parsed and re-emitted chunk-for-chunk by the writer.
   */
  binaryChunks: Map<number, string[]>;
}

export function createCompactOleFrameStore(
  initialCapacity = 8,
): CompactOleFrameStore {
  const cap = Math.max(1, initialCapacity);
  return {
    count: 0,
    base: createBaseColumns(cap),
    floats: new Float32Array(cap * OLE_FLOATS),
    ints: new Int32Array(cap * OLE_INTS),
    present: new Uint8Array(cap),
    objectDescriptions: new Map(),
    terminators: new Map(),
    binaryChunks: new Map(),
  };
}

function ensureOleCap(s: CompactOleFrameStore, needed: number): void {
  if (needed <= s.base.capacity) return;
  const c = growBaseColumns(s.base, needed);
  const f = new Float32Array(c * OLE_FLOATS); f.set(s.floats);
  const i = new Int32Array(c * OLE_INTS); i.set(s.ints);
  const p = new Uint8Array(c); p.set(s.present);
  s.floats = f; s.ints = i; s.present = p;
}

export function appendOleFrame(
  s: CompactOleFrameStore,
  table: SharedStringTable,
  e: OleFrameEntity,
): number {
  const index = s.count;
  ensureOleCap(s, index + 1);
  s.count++;
  writeBase(s.base, table, index, e);

  const f = index * OLE_FLOATS;
  s.floats[f + 0] = e.upperLeft.x;
  s.floats[f + 1] = e.upperLeft.y;
  s.floats[f + 2] = e.upperLeft.z;
  s.floats[f + 3] = e.lowerRight.x;
  s.floats[f + 4] = e.lowerRight.y;
  s.floats[f + 5] = e.lowerRight.z;

  const n = index * OLE_INTS;
  s.ints[n + 0] = e.oleVersion ?? 0;
  s.ints[n + 1] = e.oleObjectType ?? 0;
  s.ints[n + 2] = e.tileModeDescriptor ?? 0;
  s.ints[n + 3] = e.drawAspect ?? 0;
  s.ints[n + 4] = e.binaryByteCount ?? 0;

  let present = 0;
  if (e.oleVersion !== undefined) present |= OLE_HAS_VERSION;
  if (e.oleObjectType !== undefined) present |= OLE_HAS_OBJECT_TYPE;
  if (e.tileModeDescriptor !== undefined) present |= OLE_HAS_TILE_MODE;
  if (e.drawAspect !== undefined) present |= OLE_HAS_DRAW_ASPECT;
  if (e.binaryByteCount !== undefined) present |= OLE_HAS_BYTE_COUNT;
  s.present[index] = present;

  setSparse(s.objectDescriptions, index, e.objectDescription);
  setSparse(s.terminators, index, e.terminator);
  s.binaryChunks.set(index, e.binaryChunks);
  return index;
}

export function oleFrameFromCompact(
  s: CompactOleFrameStore,
  table: SharedStringTable,
  index: number,
): OleFrameEntity {
  if (index < 0 || index >= s.count) {
    throw new RangeError(
      `oleFrameFromCompact: index ${index} out of range (count=${s.count})`,
    );
  }
  const f = index * OLE_FLOATS;
  const n = index * OLE_INTS;
  const present = s.present[index] ?? 0;
  const entity: OleFrameEntity = {
    ...readBase(s.base, table, index),
    type: "OLE Frame",
    upperLeft: {
      x: s.floats[f + 0]!,
      y: s.floats[f + 1]!,
      z: s.floats[f + 2]!,
    },
    lowerRight: {
      x: s.floats[f + 3]!,
      y: s.floats[f + 4]!,
      z: s.floats[f + 5]!,
    },
    binaryChunks: s.binaryChunks.get(index) ?? [],
  };
  if ((present & OLE_HAS_VERSION) !== 0) entity.oleVersion = s.ints[n + 0]!;
  const objectDescription = s.objectDescriptions.get(index);
  if (objectDescription !== undefined) {
    entity.objectDescription = objectDescription;
  }
  if ((present & OLE_HAS_OBJECT_TYPE) !== 0) {
    entity.oleObjectType = s.ints[n + 1]!;
  }
  if ((present & OLE_HAS_TILE_MODE) !== 0) {
    entity.tileModeDescriptor = s.ints[n + 2]!;
  }
  if ((present & OLE_HAS_DRAW_ASPECT) !== 0) {
    entity.drawAspect = s.ints[n + 3]!;
  }
  if ((present & OLE_HAS_BYTE_COUNT) !== 0) {
    entity.binaryByteCount = s.ints[n + 4]!;
  }
  const terminator = s.terminators.get(index);
  if (terminator !== undefined) entity.terminator = terminator;
  return entity;
}

export function estimateOleFrameBytesPerEntity(
  s: CompactOleFrameStore,
): number {
  const fixed = OLE_FLOATS * 4 + OLE_INTS * 4 + 1 + BASE_BYTES_PER_ENTITY;
  if (s.count === 0) return fixed;
  let payloadChars = 0;
  for (const chunks of s.binaryChunks.values()) {
    for (let i = 0; i < chunks.length; i++) payloadChars += chunks[i]!.length;
  }
  return fixed + (payloadChars * 2) / s.count;
}
